import { useState } from 'react';
import { useIntl } from 'react-intl';
import Image from 'next/image';
import { MailIcon, Tooltip, Typography } from '@eventbrite/marmalade';
import { useIsMobileDevice } from '@eventbrite/legacy-infra.feature-detection';
import { CrownIcon } from './_shared/CrownIcon';
import { PremiumOrganizerBadgeIcon } from './_shared/PremiumOrganizerBadgeIcon';
import { TopOrganizerLabel } from './_shared/TopOrganizerLabel';
import { ContactButton } from './ContactButton/ContactButton';
import { ContactModal } from './ContactButton/ContactModal';
import { ExpandableBio } from './ExpandableBio/ExpandableBio';
import { Metrics } from './Metrics/Metrics';
import { SocialLinks, socialLinksStyles } from './SocialLinks/SocialLinks';
import { useOrganizerQualitySignals } from '../useOrganizerQualitySignals';
import type { OrganizerData } from '@/types/organizer';
import { FollowButton } from '../_shared/FollowButton';
import { OrganizerBannerBackground } from '../_shared/OrganizerBannerBackground';
import { LocationBadges } from './LocationBadges';
import { ShareProfile } from './ShareProfile/ShareProfile';
import { OwnerBanner } from './OwnerBanner/OwnerBanner';
import { SuperOrganizerAnnouncementModal } from './SuperOrganizerAnnouncementModal/SuperOrganizerAnnouncementModal';
import { useMarkAnnouncementSeen } from './useMarkAnnouncementSeen';
import styles from './OrganizerInfo.module.scss';

interface Props {
  data: OrganizerData;
  isOwner: boolean;
  refreshPhase: 'pending' | 'refreshing' | 'done';
  eventbriteBaseUrl: string;
}

export default function OrganizerInfo({ data, isOwner, refreshPhase, eventbriteBaseUrl }: Props) {
  const intl = useIntl();
  const qualitySignalsQuery = useOrganizerQualitySignals(data.id);
  const qs = qualitySignalsQuery.data;
  const isSuperOrganizer = qs?.isSuperOrganizer === true;
  const isPremiumOrganizer = qs?.isPremiumOrganizer === true;
  const verifiedDisplay = Boolean(data.verified || qs?.verified);
  const hasAvatar = data.avatarUrl.trim().length > 0;
  const initial = data.name.charAt(0).toUpperCase();
  // On mobile (<=639px) use the closest smaller library text style (20px) for the
  // organizer name; tablet/desktop keep heading-md.
  const isMobileViewport = useIsMobileDevice(639);
  const nameVariant = isMobileViewport ? 'body-lg-bold' : 'heading-md';
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [isPremiumTooltipOpen, setIsPremiumTooltipOpen] = useState(false);
  const nameWords = data.name.trim().split(/\s+/);
  const nameLastWord = nameWords[nameWords.length - 1];
  const namePrefix = nameWords.slice(0, -1).join(' ');
  const markAnnouncementSeen = useMarkAnnouncementSeen();
  const showAnnouncement =
    !announcementDismissed &&
    isOwner &&
    isSuperOrganizer &&
    qs?.superOrganizerAnnouncementSeen === false;

  const handleAnnouncementClose = () => {
    setAnnouncementDismissed(true);
    markAnnouncementSeen.mutate(data.id);
  };

  const editProfileUrl = `${eventbriteBaseUrl}/organizations/info/profile/${data.id}`;

  return (
    <>
      {isOwner && refreshPhase !== 'pending' && (
        <OwnerBanner editProfileUrl={editProfileUrl} refreshPhase={refreshPhase} />
      )}
      <OrganizerBannerBackground
        className={styles.cover}
        coverUrl={data.coverUrl}
        avatarUrl={data.avatarUrl}
        imageTestId="organizer-cover-background"
      />

      <div className={styles.headerWrap}>
        <div className={styles.header}>
          <div className={styles.avatarColumn}>
            <div className={styles.avatar}>
              {hasAvatar ? (
                <Image
                  src={data.avatarUrl}
                  alt={data.name}
                  fill
                  sizes="(max-width: 639px) 5.5rem, 12.5rem"
                />
              ) : (
                <span className={styles.avatarInitial}>{initial}</span>
              )}
              {isSuperOrganizer && (
                <span className={styles.avatarBadge}>
                  <CrownIcon />
                </span>
              )}
            </div>
            {isSuperOrganizer && <TopOrganizerLabel />}
          </div>

          <div className={styles.info}>
            <div className={styles.infoTop}>
              <div className={styles.infoContent}>
                <div
                  className={`${styles.nameRow} ${isSuperOrganizer ? styles.superOrganizerNameRow : ''}`}
                >
                  {isSuperOrganizer && (
                    <span className={styles.mobileTopOrganizerLabel}>
                      {intl.formatMessage({ id: 'Top Organizer', defaultMessage: 'Top Organizer' })}
                    </span>
                  )}
                  <Typography variant={nameVariant} className={styles.nameText}>
                    {isPremiumOrganizer ? (
                      <>
                        {namePrefix && `${namePrefix} `}
                        {/* keep the last word + badge together so the badge never wraps alone */}
                        <span className={styles.premiumNameEnd}>
                          {nameLastWord}
                          <Tooltip
                            content={intl.formatMessage({
                              id: 'This organizer has an Eventbrite Premium subscription.',
                              defaultMessage:
                                'This organizer has an Eventbrite Premium subscription.',
                            })}
                            className={styles.premiumTooltip}
                            providerProps={{ delayDuration: 0 }}
                            rootProps={{
                              open: isPremiumTooltipOpen,
                              onOpenChange: setIsPremiumTooltipOpen,
                            }}
                            contentProps={{ side: 'top', sideOffset: 6, avoidCollisions: false }}
                          >
                            <span
                              className={styles.premiumBadgeTrigger}
                              onClick={() => setIsPremiumTooltipOpen((open) => !open)}
                            >
                              <PremiumOrganizerBadgeIcon
                                label={intl.formatMessage({
                                  id: 'Premium organizer',
                                  defaultMessage: 'Premium organizer',
                                })}
                              />
                            </span>
                          </Tooltip>
                        </span>
                      </>
                    ) : (
                      data.name
                    )}
                  </Typography>
                </div>
                <div className={styles.locationsBlock}>
                  <LocationBadges locations={data.locations} />
                </div>
                <div className={styles.metricsBlock}>
                  <Metrics
                    organizerId={data.id}
                    hideAttendeeCount={data.hideAttendeeCount}
                    hideTotalEvents={data.hideTotalEvents}
                  />
                </div>
                <div className={styles.bioBlock}>
                  <ExpandableBio html={data.bio ?? ''} />
                </div>
                <div className={styles.socialBlock}>
                  <SocialLinks socials={data.socials}>
                    <button
                      type="button"
                      className={`${socialLinksStyles.socialBtn} ${styles.mobileContactButton}`}
                      onClick={() => setIsContactModalOpen(true)}
                      aria-label="Contact organizer"
                    >
                      <MailIcon aria-hidden />
                    </button>
                  </SocialLinks>
                </div>
              </div>

              <div className={styles.actions}>
                <FollowButton organizerId={data.id} eventbriteBaseUrl={eventbriteBaseUrl} />
                <div className={styles.contactAction}>
                  <ContactButton organizerId={data.id} eventbriteBaseUrl={eventbriteBaseUrl} />
                </div>
                <ShareProfile organizer={data} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ContactModal
        isShown={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        organizerId={data.id}
      />
      <SuperOrganizerAnnouncementModal
        isShown={showAnnouncement}
        onClose={handleAnnouncementClose}
        organizer={data}
        isVerified={verifiedDisplay}
      />
    </>
  );
}
